import { inject } from "mobx-react";
import { useMemo } from "react";
import * as React from "react";
import { useHistory } from "react-router";

import { COAST_CATEGORY_PLACEHOLDER } from "@inferno/renderer-shared-core";
import { CoastFeedCategorySelector } from "./CoastFeedCategorySelector.component";
import { CoastFeedDateSelector } from "./CoastFeedDateSelector.component";
import "./CoastFeedNavigation.style.scss";
import type { Store } from "@inferno/renderer-shared-core";

interface CoastFeedNavigationProps {
  displayHostCategories?: boolean;
  displayDateSelect?: boolean;
  store?: Store;
  children?: React.ReactNode;
}

const getStartYear = (isInTheNewsPage: boolean, isGuestsPage: boolean) => {
  if (isInTheNewsPage) {
    return 2009;
  }

  if (isGuestsPage) {
    return 2008;
  }

  return 2003;
};

export const CoastFeedNavigation = inject("store")(
  ({ store, displayHostCategories = true, displayDateSelect = true, children }: CoastFeedNavigationProps) => {
    if (!store) {
      return null;
    }

    const history = useHistory();

    const { routeParams, request } = store;
    const curPath = request.path;

    const selectedCategory = routeParams.coastCategory;
    const selectedMonth = routeParams.month;
    const selectedYear = routeParams.year;

    const isInTheNewsPage = useMemo(() => curPath.startsWith("/inthenews/"), [curPath]);
    const isGuestsPage = useMemo(() => curPath.startsWith("/guests/"), [curPath]);
    const startYear = useMemo(() => getStartYear(isInTheNewsPage, isGuestsPage), [isGuestsPage, isInTheNewsPage]);

    const handleChange = useMemo(
      () => (e: React.FormEvent<HTMLSelectElement>) => {
        const property = e.currentTarget.name;
        const value = e.currentTarget.value;

        let category = selectedCategory;
        let month = Number(selectedMonth) || null;
        let year = Number(selectedYear) || null;

        const [, basePath] = /^(\/[^\/]*\/)/.exec(curPath) || [null, ""];
        let path = basePath || "/";

        switch (property) {
          case "year":
            year = Number(value);
            break;
          case "month":
            month = Number(value);
            break;
          case "category":
            category = value;
            break;
        }

        if (!isInTheNewsPage && ((!!category && category !== COAST_CATEGORY_PLACEHOLDER) || year)) {
          path += `${category || COAST_CATEGORY_PLACEHOLDER}/`;
        }

        if (year) {
          path += `${year}/`;

          if (month) {
            path += `${month}/`;
          }
        }

        history.push(path);
      },
      [selectedCategory, selectedMonth, selectedYear, curPath, isInTheNewsPage, history],
    );

    return (
      <div className="component-coast-feed-navigation">
        {displayDateSelect && (
          <CoastFeedDateSelector
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            handleChange={handleChange}
            yearsStartFrom={startYear}
          />
        )}

        {!isInTheNewsPage && (
          <CoastFeedCategorySelector
            displayHostCategories={!!displayHostCategories}
            selectedCategory={selectedCategory}
            handleChange={handleChange}
          />
        )}
        {children}
      </div>
    );
  },
);
